import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
  Avatar,
  Alert,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { styled } from '@mui/material/styles';
import { fetchWrapper } from '@/lib/fetch-wrapper';
import { urls } from '@/urls';
import { COLORS } from '@/theme/colors';
import { T, statusTone } from '@/theme/tokens';
import { Pill } from '@/components/ds';
import { DisputeDialog } from '@/components/dispute-dialog/dispute-dialog';
import DisputesModal from '@/components/disputes-table/disputes-modal';
import { getUser } from '@/lib/common';
import { ActionCenterItemV2 } from '@cloudcost/shared-types/action-center-v2';
import { Loader } from '@/components/loader/loader';
import CostPerDayChart from '@/components/shared-charts/cost-per-day-chart';
import RecommendationsListModal from '@/screens/attestation/components/recommendations-list-modal';

const StyledDialog = styled(Dialog)<{ hasOpenChildModal?: boolean; customZIndex?: number }>(({ theme, hasOpenChildModal, customZIndex }) => ({
  zIndex: customZIndex ? customZIndex : 1300,
  '& .MuiDialog-container': {
    zIndex: customZIndex ? customZIndex : 1300,
  },
  '& .MuiDialog-paper': {
    backgroundColor: 'transparent',
    boxShadow: 'none',
    maxWidth: '1200px',
    width: '100%',
    margin: '32px auto',
    padding: 0,
    overflow: 'hidden',
    zIndex: customZIndex ? customZIndex + 1 : 1301,
    position: 'relative',
    // When child modal is open, disable pointer events so clicks go through to the dispute dialog
    pointerEvents: hasOpenChildModal ? 'none' : 'auto',
  },
  '& .MuiBackdrop-root': {
    zIndex: customZIndex ? customZIndex - 1 : 1299,
    // When child modal is open, make backdrop less prominent but keep it
    opacity: hasOpenChildModal ? 0.3 : 0.5,
  },
}));

const ModalPaper = styled(Paper)<{ hasOpenChildModal?: boolean; customZIndex?: number }>(({ theme, hasOpenChildModal, customZIndex }) => ({
  backgroundColor: T.bg,
  borderRadius: `${T.radius}px`,
  boxShadow: T.popShadow,
  height: 'auto',
  maxHeight: 'calc(100vh - 64px)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  position: 'relative',
  zIndex: customZIndex ? `${customZIndex + 1} !important` : 1301,
  // When child modal is open, disable pointer events so clicks go through
  pointerEvents: hasOpenChildModal ? 'none' : 'auto',
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  backgroundColor: T.surface,
  borderBottom: `1px solid ${T.border}`,
  padding: '16px 24px',
  '& .MuiTypography-root': {
    fontSize: '1.2rem',
    fontWeight: 900,
    letterSpacing: '-.2px',
    color: T.text,
  },
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  backgroundColor: T.bg,
  overflow: 'auto',
  flex: 1,
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: T.divider,
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: T.neutralGrey,
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: T.textFaint3,
  },
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  padding: '16px 24px',
  backgroundColor: T.surface,
  borderTop: `1px solid ${T.border}`,
}));

const StyledButton = styled(Button)(({ theme }) => ({
  backgroundColor: T.primary,
  color: 'white',
  textTransform: 'none',
  fontWeight: 800,
  borderRadius: `${T.radiusSm}px`,
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: T.primaryHover,
    boxShadow: 'none',
  },
}));

const CancelButton = styled(Button)(({ theme }) => ({
  backgroundColor: T.surface,
  color: T.textMut,
  textTransform: 'none',
  fontWeight: 700,
  borderRadius: `${T.radiusSm}px`,
  border: `1px solid ${T.borderInput}`,
  '&:hover': {
    backgroundColor: T.neutralTint,
    borderColor: T.borderInput,
  },
}));

// ---- Design-language helpers (CloudCost.dc.html) ---------------------------
const cardSx = {
  mb: 2,
  bgcolor: T.surface,
  border: `1px solid ${T.border}`,
  borderRadius: `${T.radius}px`,
  boxShadow: T.cardShadow,
} as const;

const cardContentSx = { py: 1.75, px: 2, '&:last-child': { pb: 1.75 } } as const;

const sectionHeaderSx = {
  fontWeight: 900,
  color: T.text,
  mb: 1.5,
  fontSize: '0.95rem',
  letterSpacing: '-.2px',
} as const;

const kvLabelStyle: React.CSSProperties = {
  color: T.textFaint,
  fontWeight: 700,
  fontSize: '0.8125rem',
  whiteSpace: 'nowrap',
};
const kvValueStyle: React.CSSProperties = {
  color: T.text,
  fontWeight: 700,
  fontSize: '0.8125rem',
  textAlign: 'right',
  wordBreak: 'break-word',
};

/** Simple label/value row: faint label left, dark value right. */
function DetailRow({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      gap={2}
      sx={{ mb: 0.9 }}>
      <span style={kvLabelStyle}>{label}</span>
      <span style={kvValueStyle}>{value}</span>
    </Box>
  );
}

/** Row whose value is a custom node (link, avatar, pill…). */
function DetailRowNode({
  label,
  children,
  align = 'center',
}: {
  label: string;
  children: React.ReactNode;
  align?: 'center' | 'flex-start';
}) {
  return (
    <Box
      display="flex"
      alignItems={align}
      justifyContent="space-between"
      gap={2}
      sx={{ mb: 0.9 }}>
      <span style={{ ...kvLabelStyle, paddingTop: align === 'flex-start' ? 2 : 0 }}>
        {label}
      </span>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', minWidth: 0 }}>
        {children}
      </Box>
    </Box>
  );
}

interface ResourceDetails {
  id: number;
  resource_name: string;
  resource_type: string;
  identifier: string;
  sku: string | null;
  tier: string | null;
  status: string;
  managed_by: string | null;
  assigned_owner_id: number | null;
  assigned_owner_username: string | null;
  assigned_owner_photo: string | null;
  tech_owner_id: number | null;
  tech_owner_username: string | null;
  tech_owner_photo: string | null;
  proposed_owner_id: number | null;
  proposed_owner_username: string | null;
  subscription_id: string;
  subscription_name: string;
  resource_group_id: number;
  resource_group_name: string;
  system_id: number | null;
  system_name: string | null;
  department_id: number | null;
  department_name: string | null;
  cloud_account_id: number;
  cloud_account_name: string;
  provider_name: string;
  tenant_id: string | null;
  azure_tenant_id: string | null;
  tags: Array<{ key: string; value: string }>;
  date_created: string | null;
  date_updated: string | null;
  updated_by_username: string | null;
  disputes_count: number;
  attestation_status: string | null;
}

interface ResourceDetailsModalProps {
  open: boolean;
  onClose: () => void;
  resourceId: number | null;
  currency?: string;
  zIndex?: number;
  openedFromDispute?: boolean;
}

const ResourceDetailsModal = ({
  open,
  onClose,
  resourceId,
  currency = 'USD',
  zIndex,
  openedFromDispute = false,
}: ResourceDetailsModalProps) => {
  const [resourceDetails, setResourceDetails] = useState<ResourceDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [disputeDialogOpen, setDisputeDialogOpen] = useState(false);
  const [disputeModalOpen, setDisputeModalOpen] = useState(false);
  const [dispute, setDispute] = useState<any[]>([]);
  const [actionCenterItem, setActionCenterItem] = useState<ActionCenterItemV2 | null>(null);
  const [responsibleUserId, setResponsibleUserId] = useState<number | null>(null);
  const [availableUsers, setAvailableUsers] = useState<any[]>([]);
  const [recommendationsModalOpen, setRecommendationsModalOpen] = useState(false);
  const [recommendations, setRecommendations] = useState<any[]>([]);
  const [parentResourceId, setParentResourceId] = useState<number | null>(null);
  const [parentResourceModalOpen, setParentResourceModalOpen] = useState(false);
  const [disputeSelectionOpen, setDisputeSelectionOpen] = useState(false);
  const [availableDisputes, setAvailableDisputes] = useState<any[]>([]);

  const user = getUser();

  /**
   * Fetches recommendations for this resource (used for tooltip and modal)
   */
  const fetchRecommendations = React.useCallback(async () => {
    if (!resourceId) return;

    try {
      // Fetch recommendations for this resource
      const resourceRecommendations = await fetchWrapper.get(
        `${urls.DevOps.getRecommendationsByResourceId.replace(':resourceId', resourceId.toString())}?statusFilter=active&currency=${currency}`,
      );

      // Ensure we have an array
      const recommendationsArray = Array.isArray(resourceRecommendations) 
        ? resourceRecommendations 
        : [];
      
      setRecommendations(recommendationsArray);
    } catch (error) {
      console.error('Error fetching recommendations:', error);
      setRecommendations([]);
    }
  }, [resourceId, currency]);

  useEffect(() => {
    if (!resourceId || !open) {
      setResourceDetails(null);
      setError(null);
      setRecommendations([]);
      return;
    }

    setLoading(true);
    setError(null);

    fetchWrapper
      .get(
        `${urls.DevOps.getResourcePopupDetails.replace(':id', resourceId.toString())}?currency=${currency}`,
      )
      .then((data) => {
        setResourceDetails(data);
        setLoading(false);
        fetchRecommendations();
      })
      .catch((error) => {
        setError('Failed to load resource details');
        console.error(error);
        setLoading(false);
      });
  }, [resourceId, open, currency, fetchRecommendations]);

  // Note: We don't auto-close the resource details modal when dispute dialog opens
  // because that would unmount the component and also close the dispute dialog.
  // Instead, we close it when the dispute dialog/modal is closed (see onClose handlers)

  /**
   * Opens dispute modal if dispute exists, otherwise opens dispute creation dialog
   */
  const handleOpenDisputeDialog = async () => {
    if (!resourceDetails || !resourceId) return;

    try {
      setLoading(true);

      // Check if there's a dispute already linked to this resource
      // Use the dedicated endpoint that filters by user permissions
      try {
        const disputesForResource = await fetchWrapper.get(
          urls.DevOps.getDisputesByResourceId.replace(':resourceId', resourceId.toString())
        );

        if (disputesForResource && disputesForResource.length > 0) {
          // If there are multiple disputes, show selection dialog
          if (disputesForResource.length > 1) {
            setAvailableDisputes(disputesForResource);
            setDisputeSelectionOpen(true);
            setLoading(false);
            return;
          }

          // If only one dispute, open it directly
          const firstDispute = disputesForResource[0];
          const response = await fetchWrapper.get(
            urls.DevOps.getDispute.replace(':id', String(firstDispute.id)),
          );
          setDispute([response.dispute]);
          setDisputeModalOpen(true);
          setLoading(false);
          return;
        }
      } catch (error) {
        console.error('Error fetching disputes for resource:', error);
      }

      // Fetch available users
      const users = await fetchWrapper.get(urls.DevOps.getUsersAll);
      setAvailableUsers(users);

      // Use assigned_owner_id from resource details as responsible user
      // If no assigned owner, we'll let the user select
      setResponsibleUserId(resourceDetails.assigned_owner_id);
      setActionCenterItem(null);

      setDisputeDialogOpen(true);
      setLoading(false);
    } catch (error) {
      console.error('Error opening dispute dialog:', error);
      setLoading(false);
    }
  };

  /**
   * Handles selection of a dispute from the list
   */
  const handleSelectDispute = async (disputeId: number) => {
    try {
      setLoading(true);
      const response = await fetchWrapper.get(
        urls.DevOps.getDispute.replace(':id', String(disputeId)),
      );
      setDispute([response.dispute]);
      setDisputeSelectionOpen(false);
      setDisputeModalOpen(true);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching selected dispute:', error);
      setLoading(false);
    }
  };

  /**
   * Handles clicking on "Managed By" to open parent resource modal
   */
  const handleOpenParentResource = async () => {
    if (!resourceDetails?.managed_by) return;

    try {
      setLoading(true);
      const encodedIdentifier = encodeURIComponent(resourceDetails.managed_by);
      const response = await fetchWrapper.get(
        urls.DevOps.getResourceIdByIdentifier.replace(':identifier', encodedIdentifier),
      );
      
      if (response?.id) {
        setParentResourceId(response.id);
        setParentResourceModalOpen(true);
      } else {
        setError('Parent resource not found');
      }
    } catch (error) {
      console.error('Error fetching parent resource:', error);
      setError('Failed to load parent resource');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetches and displays recommendations for this resource
   */
  const handleOpenRecommendations = async () => {
    if (!resourceDetails || !resourceId) return;

    // If recommendations are already loaded, just open the modal
    if (recommendations.length > 0) {
      setRecommendationsModalOpen(true);
      return;
    }

    // Otherwise fetch and then open
    await fetchRecommendations();
    setRecommendationsModalOpen(true);
  };

  /**
   * Handles dispute submission - creates dispute and opens modal
   */
  const handleDisputeSubmit = async (title: string, receiverId: number) => {
    if (!resourceId) return;

    try {
      setLoading(true);

      // Create dispute with resource_id linked directly
      await fetchWrapper.post(urls.DevOps.createDisputeV2, {
        id_receiver: receiverId,
        sender: user?.username,
        title: title,
        type: 'Resource',
        resource_ids: [resourceId], // Pass resource_id as array
        action_center_id: null, // Not using action center for resource disputes
      });

      const maxDisputeData = await fetchWrapper.get(urls.DevOps.getMaxDispute);
      const disputeId = maxDisputeData[0]?.disputeid;

      if (disputeId) {
        const disputesList = await fetchWrapper.get(urls.DevOps.disputesList);
        const createdDispute = disputesList.filter(
          (row: any) => row.id === disputeId,
        );

        if (createdDispute.length > 0) {
          setDispute(createdDispute);
          setDisputeDialogOpen(false);
          setDisputeModalOpen(true);
        }
      }
    } catch (error) {
      console.error('Error creating dispute:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const formatValue = (value: any) => {
    if (value === null || value === undefined || value === '') {
      return 'N/A';
    }
    return value;
  };

  if (!open) return null;

  return (
    <>
      <StyledDialog 
        open={open} 
        onClose={onClose} 
        maxWidth="xl"
        hasOpenChildModal={disputeDialogOpen || disputeModalOpen}
        customZIndex={zIndex}
        slotProps={{
          root: {
            style: { 
              zIndex: zIndex ? zIndex : 1300,
              position: 'fixed'
            }
          },
          backdrop: {
            style: {
              zIndex: zIndex ? zIndex - 1 : 1299
            }
          }
        }}
      >
        <ModalPaper hasOpenChildModal={disputeDialogOpen || disputeModalOpen} customZIndex={zIndex}>
          <StyledDialogTitle>
            <Typography variant="h6">
              {resourceDetails?.resource_name || 'Resource Details'}
            </Typography>
          </StyledDialogTitle>

          <StyledDialogContent>
            {loading && <Loader />}
            {error && (
              <Alert severity="error" sx={{ m: 2 }}>
                {error}
              </Alert>
            )}
            {!loading && !error && resourceDetails && (
              <>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {/* Column 1 */}
                  <Grid item xs={12} md={6}>
                    <Card sx={cardSx} elevation={0}>
                      <CardContent sx={cardContentSx}>
                        <Typography sx={sectionHeaderSx}>Organization</Typography>
                        <Box>
                          <DetailRow
                            label="Department Name"
                            value={formatValue(resourceDetails.department_name)}
                          />
                          <DetailRow
                            label="System Name"
                            value={formatValue(resourceDetails.system_name)}
                          />
                          {resourceDetails.assigned_owner_username && (
                            <DetailRowNode label="Assigned Owner">
                              <Box display="flex" alignItems="center" gap={0.75}>
                                {resourceDetails.assigned_owner_photo && (
                                  <Avatar
                                    src={resourceDetails.assigned_owner_photo}
                                    sx={{ width: 22, height: 22 }}
                                  />
                                )}
                                <span style={kvValueStyle}>
                                  {resourceDetails.assigned_owner_username}
                                </span>
                              </Box>
                            </DetailRowNode>
                          )}
                        </Box>
                      </CardContent>
                    </Card>

                    <Card sx={cardSx} elevation={0}>
                      <CardContent sx={cardContentSx}>
                        <Typography sx={sectionHeaderSx}>Technical Hierarchy</Typography>
                        <Box>
                          <DetailRow
                            label="Provider Name"
                            value={formatValue(resourceDetails.provider_name)}
                          />
                          <DetailRow
                            label="Cloud Account Name"
                            value={formatValue(resourceDetails.cloud_account_name)}
                          />
                          <DetailRow
                            label="Subscription Name"
                            value={formatValue(resourceDetails.subscription_name)}
                          />
                          <DetailRow
                            label="Resource Group Name"
                            value={formatValue(resourceDetails.resource_group_name)}
                          />
                          <DetailRow
                            label="Resource Name"
                            value={formatValue(resourceDetails.resource_name)}
                          />
                          {(resourceDetails.azure_tenant_id || resourceDetails.tenant_id) && (
                            <Box display="flex" alignItems="center" gap={0.5} mt={1.5}>
                              {resourceDetails.identifier ? (
                                <Link
                                  href={`https://portal.azure.com/#@${resourceDetails.azure_tenant_id || resourceDetails.tenant_id}/resource${resourceDetails.identifier}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{
                                    color: T.primary,
                                    fontWeight: 700,
                                    textDecoration: 'none',
                                    fontSize: '0.8125rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    '&:hover': {
                                      textDecoration: 'underline',
                                    },
                                  }}>
                                  Azure Portal
                                  <OpenInNewIcon sx={{ fontSize: '0.875rem' }} />
                                </Link>
                              ) : resourceDetails.subscription_id ? (
                                <Link
                                    href={`https://portal.azure.com/#@${resourceDetails.azure_tenant_id || resourceDetails.tenant_id}/resource/subscriptions/${resourceDetails.subscription_id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{
                                    color: T.primary,
                                    fontWeight: 700,
                                    textDecoration: 'none',
                                    fontSize: '0.8125rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    '&:hover': {
                                      textDecoration: 'underline',
                                    },
                                  }}>
                                  Azure Portal (Subscription)
                                  <OpenInNewIcon sx={{ fontSize: '0.875rem' }} />
                                </Link>
                              ) : null}
                            </Box>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                    <Card sx={cardSx} elevation={0}>
                      <CardContent sx={cardContentSx}>
                        <Typography sx={sectionHeaderSx}>Technical Details</Typography>
                        <Box>
                          <DetailRow
                            label="Resource Type"
                            value={formatValue(resourceDetails.resource_type)}
                          />
                          <DetailRow label="SKU" value={formatValue(resourceDetails.sku)} />
                          <DetailRow label="Tier" value={formatValue(resourceDetails.tier)} />
                          <DetailRowNode label="Managed By">
                            {resourceDetails.managed_by ? (
                              <Link
                                component="button"
                                variant="body2"
                                onClick={handleOpenParentResource}
                                sx={{
                                  color: T.primary,
                                  fontWeight: 700,
                                  fontSize: '0.8125rem',
                                  cursor: 'pointer',
                                  textDecoration: 'none',
                                  background: 'none',
                                  border: 'none',
                                  padding: 0,
                                  textAlign: 'right',
                                  '&:hover': {
                                    textDecoration: 'underline',
                                  },
                                }}>
                                {resourceDetails.managed_by}
                              </Link>
                            ) : (
                              <span style={kvValueStyle}>
                                {formatValue(resourceDetails.managed_by)}
                              </span>
                            )}
                          </DetailRowNode>
                          <DetailRow
                            label="Date Created"
                            value={formatDate(resourceDetails.date_created)}
                          />
                          <DetailRow
                            label="Date Updated"
                            value={formatDate(resourceDetails.date_updated)}
                          />
                          {resourceDetails.tags && resourceDetails.tags.length > 0 && (
                            <Box sx={{ mt: 1.25 }}>
                              <span style={{ ...kvLabelStyle, display: 'block', marginBottom: 6 }}>
                                Tags
                              </span>
                              <Box display="flex" flexWrap="wrap" gap={0.75}>
                                {resourceDetails.tags.map((tag, index) => (
                                  <Chip
                                    key={index}
                                    label={`${tag.key}: ${tag.value}`}
                                    size="small"
                                    sx={{
                                      bgcolor: T.neutralTint,
                                      color: T.textMut,
                                      fontWeight: 700,
                                      fontSize: '0.72rem',
                                      border: `1px solid ${T.border}`,
                                    }}
                                  />
                                ))}
                              </Box>
                            </Box>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Column 2 */}
                  <Grid item xs={12} md={6}>
                    <Card sx={cardSx} elevation={0}>
                      <CardContent sx={cardContentSx}>
                        <Typography sx={sectionHeaderSx}>Additional Information</Typography>
                        <Box>
                          <Box sx={{ mb: 0.9 }}>
                            <span style={{ ...kvLabelStyle, display: 'block', marginBottom: 4 }}>
                              Recommendations
                            </span>
                            {recommendations.length > 0 ? (
                              <Box>
                                {recommendations.map((rec: any) => (
                                  <Link
                                    key={rec.id}
                                    component="button"
                                    variant="body2"
                                    onClick={handleOpenRecommendations}
                                    sx={{
                                      color: T.primary,
                                      fontWeight: 700,
                                      fontSize: '0.8125rem',
                                      cursor: 'pointer',
                                      textDecoration: 'none',
                                      background: 'none',
                                      border: 'none',
                                      padding: 0,
                                      display: 'block',
                                      textAlign: 'left',
                                      mb: 0.35,
                                      '&:hover': { textDecoration: 'underline' },
                                    }}>
                                    • {(rec?.name && rec.name.trim()) || (rec?.description && rec.description.trim()) || 'Unnamed Recommendation'}
                                  </Link>
                                ))}
                              </Box>
                            ) : (
                              <span style={kvValueStyle}>None</span>
                            )}
                          </Box>
                          <DetailRow
                            label="Disputes Count"
                            value={resourceDetails.disputes_count || 0}
                          />
                          {resourceDetails.attestation_status && (
                            <DetailRowNode label="Attestation Status">
                              <Pill tone={statusTone(resourceDetails.attestation_status)}>
                                {formatValue(resourceDetails.attestation_status)}
                              </Pill>
                            </DetailRowNode>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                    {/* Cost Chart */}
                    <Card sx={cardSx} elevation={0}>
                      <CardContent sx={cardContentSx}>
                        <Typography sx={sectionHeaderSx}>Cost Day-by-Day</Typography>
                        <CostPerDayChart
                          filters={`resource=${resourceId}`}
                          hideAppCard={true}
                          className=""
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </>
            )}
          </StyledDialogContent>

          <StyledDialogActions>
            <CancelButton onClick={onClose} disabled={loading}>
              Close
            </CancelButton>
            {resourceDetails && (
              <StyledButton 
                onClick={handleOpenDisputeDialog} 
                disabled={loading || openedFromDispute}
                title={openedFromDispute ? 'Cannot create dispute from within a dispute' : ''}>
                Dispute
              </StyledButton>
            )}
          </StyledDialogActions>
        </ModalPaper>
      </StyledDialog>

      <DisputeDialog
        open={disputeDialogOpen}
        onClose={() => {
          setDisputeDialogOpen(false);
          setActionCenterItem(null);
          setResponsibleUserId(null);
        }}
        onSubmit={handleDisputeSubmit}
        defaultReceiverId={
          responsibleUserId && user?.id !== responsibleUserId
            ? responsibleUserId
            : null
        }
        currentUserId={user?.id || 0}
        showUserSelector={
          responsibleUserId !== null && user?.id === responsibleUserId
        }
        availableUsers={availableUsers}
        zIndex={1400}
      />

      {disputeModalOpen && dispute[0] && (
        <DisputesModal
          Dispute={{
            ...(dispute[0] as any),
            ...(actionCenterItem
              ? {
                  id: actionCenterItem.id,
                  responsible_id: actionCenterItem.responsible_id,
                  action_center_id: actionCenterItem.id,
                }
              : {}),
            id_dispute: (dispute[0] as any).id,
          }}
          IsModalOpened={disputeModalOpen}
          onCloseModal={() => {
            setDisputeModalOpen(false);
            setDispute([]);
            setActionCenterItem(null);
          }}
          zIndex={1400}
        />
      )}

      <RecommendationsListModal
        open={recommendationsModalOpen}
        onClose={() => {
          setRecommendationsModalOpen(false);
        }}
        recommendations={recommendations}
        resourceName={resourceDetails?.resource_name || 'Resource'}
      />

      {/* Dispute Selection Dialog */}
      <Dialog
        open={disputeSelectionOpen}
        onClose={() => setDisputeSelectionOpen(false)}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>Select Dispute</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            Multiple disputes are linked to this resource. Please select one to view:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {availableDisputes.map((dispute: any) => (
              <Button
                key={dispute.id}
                variant="outlined"
                fullWidth
                onClick={() => handleSelectDispute(dispute.id)}
                sx={{
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                  textTransform: 'none',
                  py: 1.5,
                  px: 2,
                  '&:hover': {
                    backgroundColor: COLORS.mutedBlue,
                    color: 'white',
                    borderColor: COLORS.mutedBlue,
                  },
                }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {dispute.subject || dispute.title || `Dispute #${dispute.id}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Status: {dispute.status || 'Unknown'} • ID: {dispute.id}
                  </Typography>
                </Box>
              </Button>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisputeSelectionOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      <ResourceDetailsModal
        open={parentResourceModalOpen}
        onClose={() => {
          setParentResourceModalOpen(false);
          setParentResourceId(null);
        }}
        resourceId={parentResourceId}
        currency={currency}
      />
    </>
  );
};

export default ResourceDetailsModal;
